import _ from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, filterHelpers, helpers, rwoMachines } from "../utils";
import { FilterSelect } from ".";

const { ADD_COUNT_MESSAGE, MAX_MACHINES_PER_ADD, addedMachinesMessage, parseAddCount } = rwoMachines;

const DefaultRWOMachine = {
   typeId: 0,
   typeName: "",
   numberOfMachines: 0,
   spindles: 0,
   hasBSide: false,
   isActive: true,
   sendMail: false
}

// Assign Pertinent Machine Data offers ACTIVE machines only - nothing to pick until one is activated (or added).
const noActiveMachinesMessage = (typeName) =>
   `No active machines for ${typeName}. Ask an admin to activate the machine number first (Admin Tools -> RWO Settings).`;

const DefaultMachineSetupType = {
   id: 0,
   name: "",
   shortName: "",
   procType: 0,
   defaultEfficiency: 0,
   defaultSpindles: 0,
   defaultOverheadCost: 0,
   rwoMachineId: null,
   isActive: true
}

export default function MachineManagement(props) {
   const [addCount, setAddCount] = useState("");
   const [addingMachines, setAddingMachines] = useState(false);
   const [departList, setDepartList] = useState([]);
   const [firstSelectedMachine, setFirstSelectedMachine] = useState(null);
   const [machineSetupList, setMachineSetupList] = useState([]);
   const [machineSetupSelected, setMachineSetupSelected] = useState(null);
   const [pertinentMachine, setPertinentMachine] = useState(null);
   const [pristineMachineSetup, setPristineMachineSetup] = useState(null);
   const [pristineRWO, setPristineRWO] = useState(null);
   const [processList, setProcessList] = useState([]);
   const [refreshScreen, setRefreshScreen] = useState(false);
   const [rWOMachinesList, setRWOMachinesList] = useState([]);
   const [savingMachineData, setSavingMachineData] = useState(false);
   const [selectedPertinentMachine, setSelectedPertinentMachine] = useState(null);
   const [showMasterMachineSetup, setShowMasterMachineSetup] = useState(false);
   const [showRWOMachineEdit, setShowRWOMachineEdit] = useState(false);

   useEffect(() => {
      document.title = `Machine Managment`;
      Promise.all([getRWOMachineList(), getMachineSetupList(), LoadAllDepts(), LoadAllProcesses()])
         .then((oldResults) => {
            //Object.assign can be used to merge multiple objects into one 
            let newResults = {};
            _.each(oldResults, (x) => Object.assign(newResults, x));
            const {
               RWOMachineTypeList,
               MachineSetupTypeList,
               DepartmentList,
               ProcessList
            } = newResults;
            setRWOMachinesList(helpers.addLabelValueToRwoTypeUnfiltered(RWOMachineTypeList));
            setMachineSetupList(helpers.addLabelValueToMachineSetupDto(MachineSetupTypeList));
            setDepartList(helpers.addLabelDepartmentList(DepartmentList));
            setProcessList(parseProcessList(ProcessList));
            //(helpers.addLabelValueToGLA());
         })
   }, [refreshScreen])

   useEffect(() => {
      // The Assign Pertinent list reads the selected type's own activeMachines, so a reloaded type list
      // (after machines are added) must replace the selection or the new numbers never show.
      setSelectedPertinentMachine((prev) => {
         const fresh = prev ? _.find(rWOMachinesList, (x) => x.typeId === prev.typeId) : null;
         return fresh && fresh.activeMachines !== prev.activeMachines ? fresh : prev;
      });
   }, [rWOMachinesList])

   function addMachines() {
      if (addingMachines) return; // one add at a time - a second click must not mint more machines
      const count = parseAddCount(addCount);
      if (!count) {
         toast.error(ADD_COUNT_MESSAGE);
         return;
      }
      const { typeId, typeName } = firstSelectedMachine;
      setAddingMachines(true);
      api.post(`Repair/AddMachines/${typeId}/${count}`)
         .then((response) => {
            if (api.toastFailure(response, "The machines could not be added.")) return;
            toast.success(addedMachinesMessage(response.data.numbers, typeName));
            setAddCount("");
            setRefreshScreen(p => !p);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => setAddingMachines(false));
   }

   const CallBackGetMachineDetails = (macType, MacNumber) => {
      api.post('Repair/GetMachineInstance', { TypeId: macType, Number: MacNumber })
         .then((response) => {
            if (response.data.success) {
               setPertinentMachine(response.data.machine);
            }
         })
         .catch(api.catchHandler);
   }

   const CreateNewRWOFormOrMachineSetup = (NewRWO) => {
      if (NewRWO) {
         if (firstSelectedMachine && firstSelectedMachine.typeId === 0 && showRWOMachineEdit) {
            return;
         }
         // override any current selected machine with the default
         // set the showing to true, and not the opposite because it could already be open
         setFirstSelectedMachine(DefaultRWOMachine);
         setShowRWOMachineEdit(true);
      } else {
         // will be a new Machine Setup  (39 = Mahan Dryer)
         if (machineSetupSelected && machineSetupSelected.Id === 39 && showMasterMachineSetup) {
            return;
         }
         setMachineSetupSelected(DefaultMachineSetupType);
         setShowMasterMachineSetup(true);
      }
   };

   function getMachineSetupList() {
      return api
         .post(`MachineSetup/GetMachineNameList/0/false`)
         .then((response) => {
            if (response.data?.success)
               return { MachineSetupTypeList: response.data?.machineTypes }
            else if (response.data?.message)
               toast.error(response.data?.message);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
   }

   function getRWOMachineList() {
      return api
         .fetch('Reference/GetRepairMachineTypes', {
         })
         .then((response) => {
            if (response) {
               return { RWOMachineTypeList: response.data?.machineTypes ?? [] }
            }
         })
         .catch(api.catchHandler);
   }

   function LoadAllDepts() {
      return api
         .fetch('Repair/LoadAllDepartments', {
         })
         .then((response) => {
            if (response) {
               if (response.data.success) return { DepartmentList: response.data.departments }
            }
         })
         .catch(api.catchHandler);
   }

   function LoadAllProcesses() {
      return api
         .fetch('MachineSetup/LoadAllProcesses', {
         })
         .then((response) => {
            if (response) {
               if (response.data.success)
                  return { ProcessList: response.data.processes }
            }
         })
         .catch(api.catchHandler);
   }

   function onChangeSelect(selected, MachineSetup) {
      if (MachineSetup) {
         if (pristineMachineSetup === null) {
            // only the initial selection should run this
            let copy = _.cloneDeep(selected);
            setPristineMachineSetup(copy);
            setMachineSetupSelected(selected);
         } else {
            // you have selected another option and you did not save
            // meaning any changes you made should be reverted
            const result = resetMachineSetupUnsaved(selected);
            if (result.success) {
               // setPristineMachineSetup(selected);
            }
         }
      }
      else {
         if (pristineRWO === null) {
            let copy = _.cloneDeep(selected);
            setPristineRWO(copy);
            setFirstSelectedMachine(selected);
         } else {
            resetRWOMachineUnsaved(selected);
         }
      }
   }

   function resetMachineSetupUnsaved(selectedMachine) {
      if (!showMasterMachineSetup) {
         return {
            success: true
         };
      }
      let finalMessage = {
         success: false
      }
      try {
         if (selectedMachine === pristineMachineSetup) {
            finalMessage.success = true;
            return finalMessage;
         } else {
            setPristineMachineSetup(
               (prev) => {
                  let newList = _.cloneDeep(machineSetupList);
                  const pristineMachineSetupIdx = _.findIndex(newList, x => x.id === prev.id);
                  newList[pristineMachineSetupIdx] = prev;
                  setMachineSetupList(newList);
                  // by creating a deep clone you are seperating this object reference from 
                  // the reference to the object that is being changed by some onChange Func
                  // without this unique reference when this function is called again 
                  // prev would show the changes made by the onChange Func
                  const pristinUnique = _.cloneDeep(selectedMachine);
                  prev = pristinUnique;
                  return prev;
               }
            )

            finalMessage.success = true;
            setMachineSetupSelected((prev) => {
               // by object reference here we see the changes intitially but they
               // should be discarded after 
               prev = selectedMachine;
               return prev
            });
            return finalMessage;
         }
      } catch (e) {
         console.error(e);
      }
   }

   function resetRWOMachineUnsaved(selectedMachine) {
      if (!showRWOMachineEdit) return { success: true };
      let finalMessage = {
         success: false
      };
      if (selectedMachine === pristineRWO) {
         finalMessage.success = true;
         return finalMessage;
      } else {
         setPristineRWO(
            (prev) => {
               let newList = _.cloneDeep(rWOMachinesList);
               const pristineRWOMachineIdx = _.findIndex(newList, x => x.typeId === prev.typeId);
               newList[pristineRWOMachineIdx] = prev;
               setRWOMachinesList(newList);
               const prisitineUnique = _.cloneDeep(selectedMachine);
               prev = prisitineUnique;
               return prev;
            }
         );
         finalMessage.success = true;
         setFirstSelectedMachine(selectedMachine);
         return finalMessage;
      }
   }

   function parseProcessList(list) {
      if (!list || !list.length) return [];
      const pList = _.chain(list)
         .orderBy((x) => [x.process])
         .map((x) => { return x })
         .value();

      return _.concat(
         _.map(pList, (p) => ({
            value: p.step,
            label: p.process,
         }))
      );
   }

   const SaveMachineSetup = (MachineSetup) => {
      if (!MachineSetup || MachineSetup.id === undefined)
         return toast.error("Error please provide a valid Machine Setup");
      if (!MachineSetup.shortName.length)
         return toast.error("Error please provide a valid shortName");
      if (MachineSetup.name.length < 3)
         return toast.error("Error please provide a valid name");
      if (!MachineSetup.rwoMachineId)
         return toast.error("Error please provide a valid RWO Machine Type or reselect your option");
      //if (MachineSetup?.procType === 0)
      //   return toast.error("Error please provide a valid Process Type or reselect your option");
      if (!MachineSetup.defaultEfficiency)
         MachineSetup.defaultEfficiency = 0;
      if (!MachineSetup.defaultOverheadCost)
         MachineSetup.defaultOverheadCost = 0;
      if (!MachineSetup.defaultSpindles)
         MachineSetup.defaultSpindles = 0;

      let payload = MachineSetup;
      if (typeof (payload.id) === "string" ? payload.id = parseInt(payload.id) : null);
      if (typeof (payload.isActive) === "undefined" ? payload.isActive = false : null);
      if (typeof (payload.rwoMachineId) === "string" ? payload.rwoMachineId = parseInt(payload.rwoMachineId) : null);
      if (typeof (payload.rwoMachineId) !== "number" ? payload.rwoMachineId = payload.rwoMachineId.value : null);
      if (typeof (payload.procType) === "string" ? payload.procType = parseInt(payload.procType) : null);
      if (typeof (payload.procType) !== "number" ? payload.procType = payload.procType.value : null);
      if (typeof (payload.defaultEfficiency) === "string" ? payload.defaultEfficiency = parseInt(payload.defaultEfficiency) : null);
      if (typeof (payload.defaultOverheadCost) === "string" ? payload.defaultOverheadCost = parseFloat(payload.defaultOverheadCost) : null);
      if (typeof (payload.defaultSpindles) === "string" ? payload.defaultSpindles = parseInt(payload.defaultSpindles) : null);

      let saveMsg = payload.id <= 0
         ? "Successfully saved new Machine Setup."
         : "Successfully updated Machine Setup.";

      let errorMsg = payload.id <= 0
         ? "Error unable to save new Machine Setup."
         : "Error unable to update Machine Setup.";

      return api
         .post('MachineSetup/SaveMachineSetupName', payload)
         .then((response) => {
            if (response && response.data) {
               if (response.data.success) {
                  setRefreshScreen(!refreshScreen);
                  setShowMasterMachineSetup(!showMasterMachineSetup);
                  setMachineSetupSelected(null);
                  setPristineMachineSetup(null);
                  toast.success(saveMsg);
               } else {
                  toast.error(response.data.message);
               }
            } else {
               toast.error(errorMsg);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
   };

   const SavePertinentMachineData = (MachineData) => {
      if (savingMachineData) return; // one save at a time
      if (!MachineData || !MachineData.typeName)
         return toast.error("Error please select valid Machine Type")

      let payload = MachineData;
      if (typeof (payload.spindleCount === "string")
         ? payload.spindleCount = parseInt(payload.spindleCount)
         : null);

      setSavingMachineData(true);
      return api
         .post('Repair/SavePertinentMachineData', payload)
         .then((response) => {
            // Re-read the active machine lists on a refusal too - a refused machine may have been deactivated since this
            // screen loaded, and the stale list would offer it again.
            setRefreshScreen(p => !p);
            // The server's sentence (a machine that is inactive or does not exist) when it sent one.
            if (api.toastFailure(response, "Error unable to save pertinent Machine Data.")) return;
            setSelectedPertinentMachine(null);
            setPertinentMachine(null);
            toast.success("Machine Data has been successfully updated.")
         })
         .catch(api.catchHandler)
         .finally(() => setSavingMachineData(false));
   }

   const SaveRWOMachine = (RWOMachine) => {
      if (!RWOMachine || RWOMachine.typeId === undefined)
         return toast.error("Error please provide a valid Machine Type");
      if (RWOMachine.typeName.length < 3)
         return toast.error("Error please provide a valid name");
      // if (!RWOMachine.spindles) return toast.error("Error please provide number of spindles");
      if (RWOMachine.numberOfMachines === null || RWOMachine.numberOfMachines === undefined)
         RWOMachine.numberOfMachines = 0;
      if (RWOMachine.spindles === null || RWOMachine.spindles === undefined)
         RWOMachine.spindles = 0;
      let payload = RWOMachine;
      if (typeof (payload.numberOfMachines) === "string" ? payload.numberOfMachines = parseInt(payload.numberOfMachines) : null);
      if (typeof (payload.spindles) === "string" ? payload.spindles = parseInt(payload.spindles) : null);

      let saveMsg = payload.id <= 0
         ? "Successfully saved new RWO Machine Type."
         : "Successfully updated RWO Machine Type.";

      let errorMsg = payload.id <= 0
         ? "Error unable to save new RWO Machine Type."
         : "Error unable to update RWO Machine Type.";

      return api
         .post('Repair/SaveMachineType', payload)
         .then((response) => {
            if (response && response.data && response.data.success) {
               setRefreshScreen(!refreshScreen);
               setShowRWOMachineEdit(!showRWOMachineEdit);
               setFirstSelectedMachine(null);
               setPristineRWO(null);
               toast.success(saveMsg)
            } else {
               toast.error(response?.message || response?.data?.message || errorMsg)
            }
         })
   };

   return (
      <DesktopContainer screenName={"Machine Managment"}>
         {/* Master RWO Machine Maintenance */}
         <div className='filter-header'>
            <Row className="mx-3">
               <Col xs={12}>
                  <h5 children="Master RWO Machine Maintenance" className="text-white" />
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody>
               <Row className="mb-2">
                  <Col xs="12" md="3" style={{ textAlign: "right", marginTop: "6px" }}>
                     <span className="boldedWriting float-end">RWO Machine Type:</span>
                  </Col>
                  <Col xs="6">
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        options={rWOMachinesList ?? []}
                        value={firstSelectedMachine ?? null}
                        onChange={(e) => onChangeSelect(e)}
                     // isSearchable
                     // isClearable
                     />
                  </Col>
                  <Col>
                     <Button
                        className="shredButton"
                        onClick={() => firstSelectedMachine
                           ? setShowRWOMachineEdit(!showRWOMachineEdit)
                           : null}
                     >EDIT
                     </Button>
                  </Col>
               </Row>
               <Row>
                  <Col xs="12" md="3"></Col>
                  <Col>
                     <Button
                        className="shredButton"
                        onClick={() => CreateNewRWOFormOrMachineSetup(true)}
                     >CREATE NEW RWO MACHINE TYPE
                     </Button>
                  </Col>
                  <Col xs="12" md="5"></Col>
               </Row>
               {firstSelectedMachine && firstSelectedMachine.typeId > 0 && !showRWOMachineEdit ?
                  <Row className="mb-2">
                     <Col xs="12" md="3" style={{ textAlign: "right", marginTop: "6px" }}>
                        <span className="boldedWriting float-end">How many:</span>
                     </Col>
                     <Col xs="6">
                        <Input
                           type="number"
                           min={1}
                           max={MAX_MACHINES_PER_ADD}
                           value={addCount}
                           disabled={addingMachines}
                           onChange={(e) => setAddCount(e.target.value)}
                        />
                     </Col>
                     <Col>
                        <Button className="shredButton" disabled={addingMachines} onClick={() => addMachines()}>
                           ADD MACHINES
                        </Button>
                     </Col>
                  </Row>
                  : null
               }
               {showRWOMachineEdit ?
                  <EditRWOMachineMaintenance
                     selectedMachine={firstSelectedMachine}
                     CallBackToSaveRWO={SaveRWOMachine} />
                  : null
               }
            </CardBody>
         </Card>

         {/* Master Machine Setup Maintenance */}
         <div className='filter-header'>
            <Row className="mx-3">
               <Col xs={12}>
                  <h5 children="Master Machine Setup Maintenance" className="text-white" />
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody>
               <Row className="mb-2">
                  <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
                     <span className="boldedWriting float-end">Machine Setup Type:</span>
                  </Col>
                  <Col xs="6">
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        options={machineSetupList ?? []}
                        value={machineSetupSelected ?? null}
                        onChange={(e) => onChangeSelect(e, true)}
                     // isSearchable
                     // isClearable
                     />
                  </Col>
                  <Col>
                     <Button
                        className="shredButton"
                        onClick={() => machineSetupSelected
                           ? setShowMasterMachineSetup(!showMasterMachineSetup)
                           : null}
                     >EDIT
                     </Button>
                  </Col>
               </Row>
               <Row className="mb-3">
                  <Col xs="12" md="3"></Col>
                  <Col>
                     <Button
                        className="shredButton"
                        onClick={() => CreateNewRWOFormOrMachineSetup(false)}
                     >CREATE NEW MACHINE SETUP TYPE
                     </Button>
                  </Col>
                  <Col xs="12" md="5"></Col>
               </Row>
               {showMasterMachineSetup ?
                  <EditMasterMachineSetupMaintenance
                     selectedMachine={machineSetupSelected}
                     RWOMachineList={rWOMachinesList}
                     ProcessList={processList}
                     CallBackToSaveSetUp={SaveMachineSetup}
                  />
                  : null
               }
            </CardBody>
         </Card>

         {/* Assign Pertinent Machine Data */}
         <div className='filter-header'>
            <Row className="mx-3">
               <Col xs={12}>
                  <h5 children="Assign Pertinent Machine Data" className="text-white" />
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody>
               <Row className="mb-2">
                  <Col xs="12" md="3" style={{ textAlign: "right", marginTop: "6px" }}>
                     <span className="boldedWriting float-end">Machine Type:</span>
                  </Col>
                  <Col xs="6">
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        options={rWOMachinesList ?? []}
                        value={selectedPertinentMachine ?? null}
                        onChange={(e) => setSelectedPertinentMachine(e)}
                     // isSearchable
                     // isClearable
                     />
                  </Col>
               </Row>
               <EditAdmin
                  DepartmentList={departList}
                  selectedPertinentMachine={selectedPertinentMachine}
                  CallBackGetMachine={CallBackGetMachineDetails}
                  MachineFromNumAndType={pertinentMachine}
                  CallBackSavePertMachine={SavePertinentMachineData}
                  saving={savingMachineData}
               />
            </CardBody>
         </Card>
      </DesktopContainer>
   );
}

function renderInput(obj, onChangeCallback, list, procs) {
   if (!obj) return;
   if (obj.type === "text" || obj.type === "number") {
      return (
         <Row key={`${obj.name}-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               {obj.name}
            </Col>
            <Col xs="6">
               <Input
                  type={obj.type ?? "text"}
                  value={obj.value || ""}
                  name={obj.label ?? ""}
                  placeholder={obj.placeHolder ?? ""}
                  onChange={(e) => onChangeCallback(e.target.name, e.target.value)}
                  disabled={obj.disable ? true : false}
               />
            </Col>
         </Row>
      );
   } else if (obj.type === "checkbox") {
      if (obj.desc) {
         return (
            <Fragment key={`${obj.name}-abt`}>
               <Row className="mt-3">
                  <Col xs="3" style={{ textAlign: "right", marginRight: "5px" }}>
                     <Label for={obj.label} style={{ marginBottom: "0px" }}>{obj.name}</Label>
                  </Col>
                  <Col xs="6">
                     <Input style={{ marginTop: "10px" }} type="checkbox"
                        name={obj.label}
                        checked={obj.value}
                        onChange={() => onChangeCallback(obj.label, !obj.value)}
                     />
                  </Col>
               </Row>
               <Row key={`${obj.name}-atbt`}>
                  <Col xs="3" style={{ textAlign: "right", marginRight: "5px", fontSize: "small" }}>
                     {`(${obj.desc})`}
                  </Col>
               </Row>
            </Fragment>
         );
      } else {
         return (
            <Row key={`${obj.name}-abt`} className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px" }}>
                  <Label for={obj.label}>{obj.name}</Label>
               </Col>
               <Col xs="6">
                  <Input type="checkbox"
                     name={obj.label}
                     checked={obj.value}
                     onChange={() => onChangeCallback(obj.label, !obj.value)}
                  />
               </Col>
            </Row>
         );
      };
   } else if (obj.type === "select") {
      if (obj.label === "procType" && procs && procs.length !== 0) {
         return (
            <Row key={`${obj.name}-xcvt`} className="mt-2">
               <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
                  <Label for={obj.label}>{obj.name}</Label>
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={procs}
                     value={obj.value}
                     onChange={(e) => onChangeCallback(obj.label, e)}
                  />
               </Col>
            </Row>
         );
      }
      else if (obj.label === "rwoMachineId" && list && list.length !== 0) {
         return (
            <Row key={`${obj.name}-xcvt`} className="mt-2">
               <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
                  <Label for={obj.label}>{obj.name}</Label>
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={list}
                     value={obj.value}
                     onChange={(e) => onChangeCallback(obj.label, e)}
                  // isClearable
                  // isSearchable
                  />
               </Col>
            </Row>
         );
      }
   }
}

// Assign Pertinent Machine Data
function EditAdmin({
   DepartmentList,
   selectedPertinentMachine,
   CallBackGetMachine,
   MachineFromNumAndType,
   CallBackSavePertMachine,
   saving
}) {
   const [embeddedItem, setEmbeddedItem] = useState({
      deptId: -1,
      deptName: "",
      id: -1,
      isActive: false,
      machineNumber: "",
      spindleCount: "",
      typeId: -1,
      typeName: ""
   });
   const [deparList, setDepartList] = useState(DepartmentList ?? []);
   const [machineCount, setMachineCount] = useState(0);
   const [machineOptionCount, setMachineOptionCount] = useState([]);
   const [refreshScreen, setRefreshScreen] = useState(false);

   useEffect(() => {
      if (!DepartmentList) return;
      if (MachineFromNumAndType && selectedPertinentMachine) {
         setDepartList(DepartmentList);
         setEmbeddedItem(MachineFromNumAndType);
      } else {
         setEmbeddedItem(null);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [MachineFromNumAndType])

   useEffect(() => {
      setMachineCount(0);
      if (selectedPertinentMachine) {
         setEmbeddedItem(null);
         // ACTIVE machines only - reactivating a machine is in Admin Tools -> RWO Settings, never here.
         const nums = _.filter((selectedPertinentMachine.activeMachines ?? "").split(","), (n) => n.trim() !== "");
         setMachineOptionCount(_.map(nums, (n) => ({ label: n.trim(), value: n.trim() })));
      } else {
         setDepartList([]);
         setMachineOptionCount([]);
      }
   }, [selectedPertinentMachine]) // refreshscreen

   function onChangeMachineNum(type, number) {
      setMachineCount(number);
      CallBackGetMachine(type, number);
   }

   function ReturnDept(MachineData) {
      if (!MachineData || MachineData?.deptId === -1) return "";
      const Id = MachineFromNumAndType?.deptId;
      const idx = _.findIndex(deparList, x => x.value === parseInt(Id))
      return deparList[idx];
   }

   const updateEmbedded = (fieldName, fieldValue) => {
      if (fieldValue === null) {
         setEmbeddedItem((prev) => {
            prev[fieldName] = !prev[fieldName]
            return prev
         })
      } else {
         setEmbeddedItem((prev) => {
            prev[fieldName] = fieldValue
            return prev
         })
      }

      setRefreshScreen(!refreshScreen);
   }

   const updateEmbeddedDepartment = (e) => {
      setEmbeddedItem((prev) => {
         if (prev) {
            prev.deptId = e.value;
            prev.deptName = e.label;
         }
         return prev;
      })
      setRefreshScreen(!refreshScreen);
   }

   return (
      <Row>
         <Col>
            <Row className="mb-2">
               <Col xs="12" md="3" style={{ textAlign: "right", marginTop: "6px" }}>
                  <span className="boldedWriting float-end">Machine #:</span>
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={machineOptionCount ?? []}
                     value={machineCount !== 0
                        ? { label: machineCount, value: machineCount }
                        : { label: "", value: 0 }}
                     onChange={(e) => onChangeMachineNum(selectedPertinentMachine.typeId, e.value)}
                  // isSearchable
                  // isClearable
                  />
                  {selectedPertinentMachine && machineOptionCount.length === 0
                     ? <div className="mt-1">{noActiveMachinesMessage(selectedPertinentMachine.typeName)}</div>
                     : null}
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs="12" md="3" style={{ textAlign: "right", marginTop: "6px" }}>
                  <span className="boldedWriting float-end">Department:</span>
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={deparList ?? []}
                     value={ReturnDept(embeddedItem)}
                     onChange={(e) => updateEmbeddedDepartment(e)}
                  // isSearchable
                  // isClearable
                  />
               </Col>
            </Row>
            <Row className="mb-2">
               <Col xs="12" md="3" style={{ textAlign: "right", marginTop: "6px" }}>
                  <span className="boldedWriting float-end"> Spindle Count:</span>
               </Col>
               <Col xs="6">
                  <Input
                     type="number"
                     name="spindleCount"
                     value={embeddedItem?.spindleCount >= 0 ? embeddedItem.spindleCount : ""}
                     onChange={(e) => updateEmbedded(e.target.name, e.target.value)}
                     placeholder=""
                     disabled={embeddedItem === null}
                  />
               </Col>
            </Row>
            <Row>
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px" }}>
                  <span className="boldedWriting float-end">Active:</span>
               </Col>
               <Col xs="6">
                  <Input type="checkbox" name={"isActive"}
                     disabled={embeddedItem === null}
                     checked={embeddedItem?.isActive || false}
                     onChange={(e) => updateEmbedded(e.target.name, null)} />
               </Col>
            </Row>
            <Row>
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", fontSize: "small" }}>
                  {`(Machine is Active)`}
               </Col>
            </Row>
            <Row>
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px" }}></Col>
               <Col>
                  <Button onClick={() => CallBackSavePertMachine(embeddedItem)} className="shredButton" disabled={saving}>
                     Save Machine Data
                  </Button>
               </Col>
            </Row>
         </Col>
      </Row>
   );
};

// Master RWO Machine Maintenance
function EditRWOMachineMaintenance({ selectedMachine, CallBackToSaveRWO }) {
   const [embeddedItem, setEmbeddedItem] = useState(selectedMachine);
   const [refreshScreen, setRefreshScreen] = useState(false);

   useEffect(() => {
      setEmbeddedItem(selectedMachine);
   }, [refreshScreen, selectedMachine]);

   const updateEmbedded = (fieldName, fieldValue) => {
      if (fieldValue === null) {
         setEmbeddedItem((prev) => {
            prev[fieldName] = !prev[fieldName]
            return prev
         })
      } else {
         setEmbeddedItem((prev) => {
            prev[fieldName] = fieldValue
            return prev
         })
      }
      setRefreshScreen(!refreshScreen);
   }

   const fieldObjects = [
      {
         name: "Machine Type ID:",
         placeHolder: "",
         type: "number",
         label: "typeId",
         value: null,
         disable: true
      },
      {
         name: "Machine Name:",
         placeHolder: "",
         type: "text",
         label: "typeName",
         value: null
      },
      {
         name: "# of Spindles:",
         placeHolder: "",
         type: "number",
         label: "spindles",
         value: null
      },
      {
         name: "Multiple Sides:",
         type: "checkbox",
         label: "hasBSide",
         value: null,
         desc: "has B side?"
      },
      {
         name: "Active Machine:",
         type: "checkbox",
         label: "isActive",
         value: null
      },
      {
         name: "Email Notification:",
         type: "checkbox",
         label: "sendMail",
         value: null,
         desc: "Send Emails on New RWO?"
      }
   ];

   _.each(fieldObjects, (x) => {
      x.value = selectedMachine[x.label] || ""
   });

   return (
      <>
         {_.map(fieldObjects, x => renderInput(x, updateEmbedded))}
         <Row>
            <Col xs="3"></Col>
            <Col xs="6">
               <Button className="shredButton" onClick={() => CallBackToSaveRWO(embeddedItem)}>Save</Button>
            </Col>
         </Row>
      </>
   );
}

// Master Machine Setup Maintenance
function EditMasterMachineSetupMaintenance({ selectedMachine, RWOMachineList, ProcessList, CallBackToSaveSetUp}) {
   const ALL_OPTION = { value: -1, label: "--- Select ---" };
   const [embeddedItem, setEmbeddedItem] = useState(selectedMachine);
   const [refreshScreen, setRefreshScreen] = useState(false);
   const [procList, setProcList] = useState([])
   const [rwoMachineList, setRwoMachineList] = useState([])
   const [filters, setFilters] = useState([
      { filterName: "process", value: ALL_OPTION },
      { filterName: "rwo", value: ALL_OPTION },
   ]);

   useEffect(() => {
      setRwoMachineList(RWOMachineList);
      setProcList(ProcessList);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [refreshScreen])

   useEffect(() => {
      const p = _.find(procList, x => x.value === selectedMachine.procType);
      const r = _.find(rwoMachineList, x => x.value === selectedMachine.rwoMachineId);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "process",
         value: p,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "rwo",
         value: r,
      });
      setFilters(newFilters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [procList])

   // if you change to a different machine setup and dont save, the selected machine must be reset 
   useEffect(() => {
      setEmbeddedItem(selectedMachine)
      // initialize filter selections
      const p = _.find(procList, x => x.value === selectedMachine.procType);
      const r = _.find(rwoMachineList, x => x.value === selectedMachine.rwoMachineId);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "process",
         value: p,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "rwo",
         value: r,
      });
      setFilters(newFilters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedMachine])

   const updateEmbedded = (fieldName, fieldValue) => {
      if (fieldValue === null) {
         setEmbeddedItem((prev) => {
            prev[fieldName] = !prev[fieldName]
            return prev
         })
      } else {
         setEmbeddedItem((prev) => {
            prev[fieldName] = fieldValue
            return prev
         })
      }
      setRefreshScreen(!refreshScreen);
   }

   function onProcessChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      setEmbeddedItem((prev) => {
         prev["procType"] = newFilterValue.value
         prev["process"] = newFilterValue.label
         return prev
      })
   }

   function onRwoChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      setEmbeddedItem((prev) => {
         prev["rwoMachineId"] = newFilterValue.value
         prev["rwoName"] = newFilterValue.label
         return prev
      })
   }

   return (
      <>
         <Row key={`id-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               Machine Setup Type ID:
            </Col>
            <Col xs="6">
               <Input
                  type={"text"}
                  value={embeddedItem.id}
                  name={"id"}
                  //onChange={(e) => updateEmbedded("id", e.target.value)}
                  disabled={true}
               />
            </Col>
         </Row>
         <Row key={`name-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               Machine Setup Name:
            </Col>
            <Col xs="6">
               <Input
                  type={"text"}
                  value={embeddedItem.name}
                  name={"name"}
                  onChange={(e) => updateEmbedded("name", e.target.value)}
               />
            </Col>
         </Row>
         <Row key={`shortName-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               Machine Short Name:
            </Col>
            <Col xs="6">
               <Input
                  type={"text"}
                  value={embeddedItem.shortName}
                  name={"shortName"}
                  onChange={(e) => updateEmbedded("shortName", e.target.value)}
               />
            </Col>
         </Row>
         <Row key={`procType-xcvt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               <Label for={"procType"}>Process Type:</Label>
            </Col>
            <Col xs="6">
               <FilterSelect
                  filterName="process"
                  options={procList}
                  value={filterHelpers.getValue(filters, "process")}
                  onChangeCallback={(e) => onProcessChange(e)}
               />
            </Col>
         </Row>
         <Row key={`defaultEfficiency-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               Default Efficiency: (%)
            </Col>
            <Col xs="6">
               <Input
                  type={"number"}
                  value={embeddedItem.defaultEfficiency}
                  name={"defaultEfficiency"}
                  onChange={(e) => updateEmbedded("defaultEfficiency", e.target.value)}
               />
            </Col>
         </Row>
         <Row key={`defaultSpindles-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               Default Spindle Count:
            </Col>
            <Col xs="6">
               <Input
                  type={"number"}
                  value={embeddedItem.defaultSpindles}
                  name={"defaultSpindles"}
                  onChange={(e) => updateEmbedded("defaultSpindles", e.target.value)}
               />
            </Col>
         </Row>
         <Row key={`defaultOverheadCost-abt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               Default OH Cost per Hour: ($)
            </Col>
            <Col xs="6">
               <Input
                  type={"text"}
                  value={embeddedItem.defaultOverheadCost}
                  name={"defaultOverheadCost"}
                  onChange={(e) => updateEmbedded("defaultOverheadCost", e.target.value)}
               />
            </Col>
         </Row>
         <Row key={`rwoMachineId-xcvt`} className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px" }}>
               <Label for={"rwoMachineId"}>RWO Machine Type:</Label>
            </Col>
            <Col xs="6">
               <FilterSelect
                  filterName="rwo"
                  options={rwoMachineList}
                  value={filterHelpers.getValue(filters, "rwo")}
                  onChangeCallback={(e) => onRwoChange(e)}
               />
            </Col>
         </Row>
         <Fragment key={`isActive-abt`}>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px" }}>
                  <Label for={"isActive"} style={{ marginBottom: "0px" }}>Active Setup:</Label>
               </Col>
               <Col xs="6">
                  <Input style={{ marginTop: "10px" }} type="checkbox"
                     name={"isActive"}
                     checked={embeddedItem.isActive}
                     onChange={() => updateEmbedded("isActive", !embeddedItem.isActive)}
                  />
               </Col>
            </Row>
            <Row key={`isActive-atbt`}>
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", fontSize: "small" }}>
                  "Is an Active Machine Setup Type"
               </Col>
            </Row>
         </Fragment>
         <Row>
            <Col xs="3"></Col>
            <Col xs="1">
               <Button className="shredButton" onClick={() => CallBackToSaveSetUp(embeddedItem)}>
                  Save
               </Button>
            </Col>
            <Col xs="3"></Col>
         </Row>
      </>
   );
}
